// src/pages/admin/AdminDashboard.tsx
import React from "react";
import { Link } from "react-router-dom";
import {
  Users,
  BookOpen,
  Video,
  DollarSign,
  TrendingUp,
  TrendingDown,
  ArrowRight,
} from "lucide-react";

export default function AdminDashboard() {
  // Mock data - replace with real API calls
  const stats = {
    totalStudents: 156,
    activeCourses: 3,
    totalLessons: 48,
    monthlyRevenue: 7834000, // in Naira
    newStudentsThisMonth: 23,
    completionRate: 67,
    outstandingPayments: 1245000,
    emailsSent: 342,
  };

  const recentActivity = [
    {
      id: 1,
      type: "student",
      message: "New student enrolled: John Doe",
      time: "5 mins ago",
    },
    {
      id: 2,
      type: "payment",
      message: "Payment received: ₦49,999",
      time: "12 mins ago",
    },
    {
      id: 3,
      type: "lesson",
      message: "New lesson uploaded: Module 2, Lesson 3",
      time: "1 hour ago",
    },
    {
      id: 4,
      type: "email",
      message: "Email sent to 45 students",
      time: "2 hours ago",
    },
  ];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Admin Dashboard
        </h1>
        <p className="text-gray-600">Overview of your platform's performance</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="text-blue-600" size={24} />
            </div>
            <span className="text-sm text-green-600 flex items-center gap-1">
              <TrendingUp size={16} />+{stats.newStudentsThisMonth}
            </span>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-1">
            {stats.totalStudents}
          </h3>
          <p className="text-sm text-gray-600">Total Students</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <BookOpen className="text-purple-600" size={24} />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-1">
            {stats.activeCourses}
          </h3>
          <p className="text-sm text-gray-600">Active Courses</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Video className="text-green-600" size={24} />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-1">
            {stats.totalLessons}
          </h3>
          <p className="text-sm text-gray-600">Total Lessons</p>
        </div>

        <div className="bg-white rounded-xl p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
              <DollarSign className="text-teal-600" size={24} />
            </div>
            <span className="text-sm text-green-600 flex items-center gap-1">
              <TrendingUp size={16} />
              12%
            </span>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-1">
            ₦{(stats.monthlyRevenue / 1000).toFixed(0)}K
          </h3>
          <p className="text-sm text-gray-600">Monthly Revenue</p>
        </div>
      </div>

      {/* Quick Actions & Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Quick Actions
          </h2>
          <div className="space-y-3">
            <Link
              to="/admin/courses"
              className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <BookOpen className="text-purple-600" size={20} />
                <span className="font-medium text-gray-900">
                  Create New Course
                </span>
              </div>
              <ArrowRight
                className="text-gray-400 group-hover:text-gray-600"
                size={20}
              />
            </Link>

            <Link
              to="/admin/lessons"
              className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Video className="text-green-600" size={20} />
                <span className="font-medium text-gray-900">Upload Lesson</span>
              </div>
              <ArrowRight
                className="text-gray-400 group-hover:text-gray-600"
                size={20}
              />
            </Link>

            <Link
              to="/admin/email"
              className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <DollarSign className="text-blue-600" size={20} />
                <span className="font-medium text-gray-900">
                  Send Email Campaign
                </span>
              </div>
              <ArrowRight
                className="text-gray-400 group-hover:text-gray-600"
                size={20}
              />
            </Link>

            <Link
              to="/admin/students"
              className="flex items-center justify-between p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors group"
            >
              <div className="flex items-center gap-3">
                <Users className="text-orange-600" size={20} />
                <span className="font-medium text-gray-900">
                  Manage Students
                </span>
              </div>
              <ArrowRight
                className="text-gray-400 group-hover:text-gray-600"
                size={20}
              />
            </Link>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Recent Activity
          </h2>
          <div className="space-y-3">
            {recentActivity.map((activity) => (
              <div
                key={activity.id}
                className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50"
              >
                <div className="w-2 h-2 bg-teal-500 rounded-full mt-2"></div>
                <div className="flex-1">
                  <p className="text-sm text-gray-900">{activity.message}</p>
                  <p className="text-xs text-gray-500 mt-1">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Financial Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
              <TrendingUp className="text-white" size={20} />
            </div>
            <h3 className="font-semibold text-gray-900">Revenue</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-1">
            ₦{(stats.monthlyRevenue / 1000000).toFixed(2)}M
          </p>
          <p className="text-sm text-green-700">This month</p>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border border-orange-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
              <TrendingDown className="text-white" size={20} />
            </div>
            <h3 className="font-semibold text-gray-900">Outstanding</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-1">
            ₦{(stats.outstandingPayments / 1000000).toFixed(2)}M
          </p>
          <p className="text-sm text-orange-700">Pending payments</p>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
              <Users className="text-white" size={20} />
            </div>
            <h3 className="font-semibold text-gray-900">Completion</h3>
          </div>
          <p className="text-3xl font-bold text-gray-900 mb-1">
            {stats.completionRate}%
          </p>
          <p className="text-sm text-blue-700">Average rate</p>
        </div>
      </div>
    </div>
  );
}
